import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Pagination,
  SxProps,
  Theme,
  Box,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  TextField,
  MenuItem,
} from '@mui/material';

interface CustomPaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
}

const paginationSx: SxProps<Theme> = {
  '& .MuiPaginationItem-root': {
    width: 42,
    height: 38,
    fontSize: '0.875rem',
    fontWeight: 500,
    borderRadius: 2,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  '& .MuiPaginationItem-root.Mui-selected': {
    backgroundColor: 'primary.main',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'primary.dark',
    },
  },
};

const containerSx: SxProps<Theme> = {
  py: 2,
  px: { xs: 1, sm: 2 },
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  rowGap: 1,
  columnGap: 2,
};

const getEntryRangeText = (
  page: number,
  pageSize: number,
  totalCount: number,
  t: TFunction
): string => {
  if (totalCount === 0) return t('pagination.noEntries');
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalCount);
  return t('pagination.showingEntries', { start, end, totalCount });
};

const CustomPagination: React.FC<CustomPaginationProps> = ({
  page,
  pageSize = 10,
  totalCount,
  pageSizeOptions = [10, 15, 25, 50],
  onPageSizeChange,
  onPageChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('general');

  const totalPages = useMemo(
    () => Math.ceil(totalCount / pageSize),
    [totalCount, pageSize]
  );

  const entryRangeText = useMemo(
    () => getEntryRangeText(page, pageSize, totalCount, t),
    [page, pageSize, totalCount, t]
  );

  return (
    <Box component="nav" sx={containerSx} aria-label="Pagination Navigation">
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flexGrow: 1 }}
        aria-live="polite"
        role="status"
      >
        {entryRangeText}
      </Typography>

      {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && totalPages > 0 && (
        <TextField
          select
          size={'small'}
          label={t('rows')}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          sx={{ width: 92 }}
          aria-label="Rows per page"
        >
          {pageSizeOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      )}
      {totalPages > 0 && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(event, newPage) => onPageChange(event, newPage - 1)}
            siblingCount={isMobile ? 0 : 1}
            boundaryCount={1}
            shape="rounded"
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
            sx={paginationSx}
          />
        </Stack>
      )}
    </Box>
  );
};

export default CustomPagination;
